import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Application, ApplicationStatus, UploadedDocument, DocumentVerificationStatus, Applicant, DuplicateApplicantMatch, ScholarshipProgram, UserRole } from '../types';
import Modal from '../components/Modal';
import ApplicantProfileView from '../components/ApplicantProfileView';
import { yearLevelCategoryOf, YEAR_LEVEL_CATEGORY_OPTIONS } from '../constants/yearLevelCategory';

const DOC_STATUS_LABEL: Record<DocumentVerificationStatus, string> = {
  [DocumentVerificationStatus.PENDING]: 'Pending review',
  [DocumentVerificationStatus.VERIFIED]: 'Verified',
  [DocumentVerificationStatus.REJECTED]: 'Rejected',
  [DocumentVerificationStatus.REQUESTING_REVISION]: 'Revision requested'
};

const DOC_STATUS_BADGE: Record<DocumentVerificationStatus, string> = {
  [DocumentVerificationStatus.PENDING]: 'badge-warning',
  [DocumentVerificationStatus.VERIFIED]: 'badge-success',
  [DocumentVerificationStatus.REJECTED]: 'badge-error',
  [DocumentVerificationStatus.REQUESTING_REVISION]: 'badge-warning'
};

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'Draft',
  [ApplicationStatus.SUBMITTED]: 'Submitted',
  [ApplicationStatus.UNDER_REVIEW]: 'Under Review',
  [ApplicationStatus.DOCUMENT_VERIFICATION]: 'Document Verification',
  [ApplicationStatus.INTERVIEW]: 'Interview',
  [ApplicationStatus.APPROVED]: 'Approved',
  [ApplicationStatus.REJECTED]: 'Rejected',
  [ApplicationStatus.NEEDS_REVISION]: 'Needs Revision'
};

const STATUS_BADGE: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'badge-secondary',
  [ApplicationStatus.SUBMITTED]: 'badge-primary',
  [ApplicationStatus.UNDER_REVIEW]: 'badge-primary',
  [ApplicationStatus.DOCUMENT_VERIFICATION]: 'badge-warning',
  [ApplicationStatus.INTERVIEW]: 'badge-warning',
  [ApplicationStatus.APPROVED]: 'badge-success',
  [ApplicationStatus.REJECTED]: 'badge-error',
  [ApplicationStatus.NEEDS_REVISION]: 'badge-warning'
};

// Drafts are the student's own in-progress work and are never shown to
// admins (server enforces this too) — excluded from both the filter and
// the decision dropdown.
const REVIEWABLE_STATUSES = Object.values(ApplicationStatus).filter((status) => status !== ApplicationStatus.DRAFT);

// Standard notice shown to the student when an application is approved —
// reviewers can use this as-is or switch to a custom message instead.
const DEFAULT_APPROVAL_MESSAGE = `Congratulations! Your application has been approved for preliminary evaluation.

Kindly submit the original, authenticated hard copies of the documents you uploaded to the Office of the Municipal Mayor, thru Ms. Kyle B. Agliwan, ECEAP Focal Person.

The submitted documents will undergo further evaluation and validation by the Screening Committee.

Please note: This preliminary approval does not guarantee final acceptance. Your application may still be disapproved if the Screening Committee finds that the information or supporting documents submitted are false, incomplete, inconsistent, ineligible, or do not comply with the program's requirements and guidelines.`;

// Once a decision is finalized, the server rejects deletion — approved
// applications have already become a Scholar record (delete that from
// Scholar Management instead), and rejected ones are kept as an audit
// trail. The Delete button is hidden for these rather than left to fail.
const DELETABLE_STATUSES = new Set([
  ApplicationStatus.SUBMITTED,
  ApplicationStatus.UNDER_REVIEW,
  ApplicationStatus.DOCUMENT_VERIFICATION,
  ApplicationStatus.INTERVIEW,
  ApplicationStatus.NEEDS_REVISION
]);

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const ApplicationReviewPage = () => {
  const { user } = useAuth();
  const isViewer = user?.role === UserRole.VIEWER;

  const [applications, setApplications] = useState<Application[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draftStatus, setDraftStatus] = useState<ApplicationStatus | ''>('');
  const [draftComments, setDraftComments] = useState('');
  const [approvalTextMode, setApprovalTextMode] = useState<'default' | 'custom'>('default');
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentActionId, setDocumentActionId] = useState<number | null>(null);
  const [viewingDocumentId, setViewingDocumentId] = useState<number | null>(null);
  const [applicantProfile, setApplicantProfile] = useState<Applicant | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  // Other applicants matching this one on ID number, phone, or
  // name + birthdate. Empty is the normal case.
  const [duplicates, setDuplicates] = useState<DuplicateApplicantMatch[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [barangaySearch, setBarangaySearch] = useState('');
  const [programs, setPrograms] = useState<ScholarshipProgram[]>([]);
  const [programFilter, setProgramFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [deletingApplication, setDeletingApplication] = useState<Application | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadApplications = async () => {
    setError('');
    try {
      const result = await apiService.getApplications({
        pageSize: 100,
        // "Pending" isn't a real ApplicationStatus — it's every status
        // except Approved/Rejected, so it's applied client-side below
        // instead of being sent to the server as a status filter.
        ...(statusFilter && statusFilter !== 'pending' ? { status: statusFilter as ApplicationStatus } : {})
      });
      setApplications(result.data);
      setTotalCount(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    apiService
      .getScholarships(1, 100)
      .then((result) => setPrograms(result.data))
      .catch(() => {
        // Non-fatal — the Program filter just won't have options if this fails.
      });
  }, []);

  const selected = applications.find((a) => a.id === selectedId) ?? null;

  // Numbered by submission order (earliest first by default) — same "who
  // submitted first" convention used in Scholar Management, reversible
  // via the sort-order toggle. Applications with no submissionDate
  // (drafts can't reach here; only possible for records an admin
  // fast-tracked without a formal submission) always sort last,
  // regardless of direction.
  const filteredApplications = applications
    .filter((application) => {
      if (nameSearch && !application.applicantName?.toLowerCase().includes(nameSearch.toLowerCase())) return false;
      if (barangaySearch) {
        const q = barangaySearch.toLowerCase();
        const matchesBarangay = application.applicantBarangay?.toLowerCase().includes(q);
        // Some applicants entered their barangay as free text under the
        // legacy address field instead of the structured barangay field —
        // fall back to matching that too, so they're still findable.
        const matchesAddress = application.applicantAddress?.toLowerCase().includes(q);
        if (!matchesBarangay && !matchesAddress) return false;
      }
      if (programFilter && application.scholarshipId !== Number(programFilter)) return false;
      if (categoryFilter && yearLevelCategoryOf(application.applicantYearLevel) !== categoryFilter) return false;
      if (
        statusFilter === 'pending' &&
        (application.status === ApplicationStatus.APPROVED || application.status === ApplicationStatus.REJECTED)
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      if (!a.submissionDate && !b.submissionDate) return 0;
      if (!a.submissionDate) return 1;
      if (!b.submissionDate) return -1;
      const direction = sortOrder === 'asc' ? 1 : -1;
      return direction * (new Date(a.submissionDate).getTime() - new Date(b.submissionDate).getTime());
    });

  const loadDocuments = async (applicationId: number) => {
    setDocumentsLoading(true);
    try {
      const docs = await apiService.getApplicationDocuments(applicationId);
      setDocuments(docs);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadApplicantProfile = async (applicantId: number) => {
    setProfileLoading(true);
    try {
      const profile = await apiService.getApplicantProfile(applicantId);
      setApplicantProfile(profile);
    } catch (err: any) {
      setError(err.message || 'Failed to load applicant profile');
    } finally {
      setProfileLoading(false);
    }
  };

  /**
   * Deliberately silent on failure. A duplicate list is advisory, so a
   * failed lookup must not put an error banner over a review the
   * reviewer can still complete perfectly well without it.
   */
  const loadDuplicates = async (applicantId: number) => {
    try {
      setDuplicates(await apiService.getApplicantDuplicates(applicantId));
    } catch (err) {
      console.error('[Review] Could not load possible duplicates', err);
      setDuplicates([]);
    }
  };

  const openReview = (application: Application) => {
    setSelectedId(application.id);
    setDraftStatus(application.status);
    setDraftComments(application.comments ?? '');
    setApprovalTextMode(application.comments === DEFAULT_APPROVAL_MESSAGE ? 'default' : 'custom');
    setApplicantProfile(null);
    setDuplicates([]);
    loadDocuments(application.id);
    loadApplicantProfile(application.applicantId);
    loadDuplicates(application.applicantId);

    // Auto-stamp "Date Received" the first time an Admin/Super Admin opens
    // this application for review — never overwrites an existing value
    // (server re-checks this too, so a second reviewer opening it later
    // can't clobber the original date).
    if (!isViewer && !application.receivedDate) {
      apiService
        .updateApplication(application.id, { receivedDate: new Date().toISOString() })
        .then((updated) => {
          setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
        })
        .catch(() => {
          // Non-fatal — the field just stays empty until the next review open.
        });
    }
  };

  const handleDecisionStatusChange = (status: ApplicationStatus) => {
    setDraftStatus(status);
    if (status === ApplicationStatus.APPROVED) {
      setApprovalTextMode('default');
      setDraftComments(DEFAULT_APPROVAL_MESSAGE);
    }
  };

  const handleSave = async () => {
    if (!selected || !draftStatus) return;
    setSaving(true);
    setError('');
    try {
      await apiService.updateApplication(selected.id, { status: draftStatus, comments: draftComments });
      setSelectedId(null);
      await loadApplications();
    } catch (err: any) {
      setError(err.message || 'Failed to update application');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDocument = async (documentId: number) => {
    setViewingDocumentId(documentId);
    try {
      const { blob } = await apiService.downloadDocument(documentId);
      const objectUrl = URL.createObjectURL(blob);
      // Reviewers should be able to inspect a document without it landing
      // in their Downloads folder — target="_blank" (no `download`
      // attribute) opens it in the browser's own PDF/image viewer instead.
      // Still a clicked <a>, not window.open(blobUrl, '_blank') directly —
      // mobile browsers (Android Chrome especially) block/ignore a popup
      // once the surrounding await has broken the user-activation window,
      // whereas a synthetic click on an anchor element isn't affected.
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoked after a delay rather than immediately — some browsers need
      // a moment to actually pick up the blob URL before it's freed.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      setError(err.message || 'Failed to open document');
    } finally {
      setViewingDocumentId(null);
    }
  };

  const handleVerifyDocument = async (documentId: number, status: DocumentVerificationStatus) => {
    if (!selected) return;
    setDocumentActionId(documentId);
    setError('');
    try {
      await apiService.verifyDocument(documentId, status);
      await loadDocuments(selected.id);
    } catch (err: any) {
      setError(err.message || 'Failed to update document');
    } finally {
      setDocumentActionId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingApplication) return;
    setDeleting(true);
    setError('');
    setNotice('');
    try {
      const result = await apiService.deleteApplication(deletingApplication.id);
      setDeletingApplication(null);
      if (result.status === 'pending') setNotice(result.message);
      await loadApplications();
    } catch (err: any) {
      setError(err.message || 'Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Applications</div>
        {/* Bulk "Delete All" has been disabled system-wide — the server
            refuses DELETE /applications regardless, and the button is
            removed so it is not offered. Per-row deletion is unaffected. */}
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Application Review</h1>
          <p>
            Review submitted applications and record a decision.{' '}
            {!loading && <strong>Total: {totalCount.toLocaleString()} applicant{totalCount === 1 ? '' : 's'}</strong>}
          </p>
        </div>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {notice && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(167, 139, 250, 0.12)',
              border: '1px solid rgba(167, 139, 250, 0.35)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}
          >
            {notice}
          </div>
        )}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
              <label htmlFor="nameSearch">Search by Name</label>
              <input id="nameSearch" placeholder="Applicant name" value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="barangaySearch">Search by Barangay</label>
              <input id="barangaySearch" value={barangaySearch} onChange={(e) => setBarangaySearch(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
              <label htmlFor="programFilter">Filter by Program</label>
              <select id="programFilter" value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
                <option value="">All programs</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="statusFilter">Filter by status</label>
              <select id="statusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="pending">Pending (not yet Approved/Rejected)</option>
                {REVIEWABLE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABEL[status]}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="categoryFilter">Filter by Group</label>
              <select id="categoryFilter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                {YEAR_LEVEL_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="sortOrder">Sort by Date Submitted</label>
              <select id="sortOrder" value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}>
                <option value="asc">Ascending (earliest first)</option>
                <option value="desc">Descending (latest first)</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : filteredApplications.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>No applications match this filter.</p>
          </div>
        ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Applicant</th>
                  <th>Scholarship</th>
                  <th>Status</th>
                  <th>Date Submitted</th>
                  <th>Date Received</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((application, index) => (
                  <tr key={application.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div>{application.applicantName ?? `Applicant #${application.applicantId}`}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{application.applicantEmail}</div>
                    </td>
                    <td>{application.scholarshipName ?? `#${application.scholarshipId}`}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[application.status]}`}>
                        {STATUS_LABEL[application.status]}
                      </span>
                    </td>
                    <td>{formatDate(application.submissionDate)}</td>
                    <td>{formatDate(application.receivedDate)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openReview(application)}>
                          Review
                        </button>
                        {!isViewer && DELETABLE_STATUSES.has(application.status) && (
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                            onClick={() => setDeletingApplication(application)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <Modal title={`Review: ${selected.applicantName} — ${selected.scholarshipName}`} onClose={() => setSelectedId(null)}>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
              <strong>Date Submitted:</strong> {formatDate(selected.submissionDate)}
              {' · '}
              <strong>Date Received:</strong> {formatDate(selected.receivedDate)}
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <strong style={{ fontSize: '0.85rem' }}>Applicant Profile</strong>
              <ApplicantProfileView
                profile={applicantProfile}
                loading={profileLoading}
                email={selected.applicantEmail}
                userId={applicantProfile?.userId}
                canManageLock={!isViewer}
              />
            </div>

            {/* Advisory. Shared phone numbers are common here - one
                household, one handset - so a match is a prompt to look,
                not evidence of a second account. */}
            {duplicates.length > 0 && (
              <div
                style={{
                  marginBottom: '1.25rem',
                  padding: '0.9rem 1rem',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--warning-text)'
                }}
              >
                <strong style={{ fontSize: '0.85rem' }}>
                  Possible duplicate {duplicates.length === 1 ? 'account' : 'accounts'} ({duplicates.length})
                </strong>
                <p style={{ margin: '0.35rem 0 0.75rem', fontSize: '0.8rem' }}>
                  These applicants share details with this one. Families often share a phone number, so check the
                  names before treating any of these as the same person.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {duplicates.map((match) => (
                    <div key={match.applicantId} style={{ fontSize: '0.8rem' }}>
                      <div style={{ fontWeight: 600 }}>{match.fullName}</div>
                      <div>{match.email}</div>
                      <div style={{ opacity: 0.85 }}>
                        {match.reasons.join(' · ')} · registered {formatDate(match.registeredAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <strong style={{ fontSize: '0.85rem' }}>Uploaded Documents</strong>
              {documentsLoading ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Loading...</p>
              ) : documents.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>No documents uploaded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.6rem' }}>
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.85rem',
                        flexWrap: 'wrap',
                        gap: '0.5rem'
                      }}
                    >
                      <div>
                        <div>{doc.documentType}</div>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ marginTop: '0.25rem' }}
                          disabled={viewingDocumentId === doc.id}
                          onClick={() => handleViewDocument(doc.id)}
                        >
                          View file
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className={`badge ${DOC_STATUS_BADGE[doc.verificationStatus]}`}>
                          {DOC_STATUS_LABEL[doc.verificationStatus]}
                        </span>
                        {!isViewer && (
                          <>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={documentActionId === doc.id}
                              onClick={() => handleVerifyDocument(doc.id, DocumentVerificationStatus.VERIFIED)}
                            >
                              Verify
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              disabled={documentActionId === doc.id}
                              onClick={() => handleVerifyDocument(doc.id, DocumentVerificationStatus.REJECTED)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isViewer ? (
              <>
                <p style={{ fontSize: '0.85rem' }}>
                  <strong>Status:</strong> {STATUS_LABEL[selected.status]}
                </p>
                {selected.comments && (
                  <p style={{ fontSize: '0.85rem' }}>
                    <strong>Comments:</strong> {selected.comments}
                  </p>
                )}
                <button className="btn btn-outline" onClick={() => setSelectedId(null)}>
                  Close
                </button>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="decisionStatus">Decision</label>
                  <select
                    id="decisionStatus"
                    value={draftStatus}
                    onChange={(e) => handleDecisionStatusChange(e.target.value as ApplicationStatus)}
                    autoFocus
                  >
                    {REVIEWABLE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {STATUS_LABEL[status]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="decisionComments">Comments (visible to the student)</label>
                  {draftStatus === ApplicationStatus.APPROVED && (
                    <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '0.25rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 400, fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name="approvalTextMode"
                          checked={approvalTextMode === 'default'}
                          onChange={() => {
                            setApprovalTextMode('default');
                            setDraftComments(DEFAULT_APPROVAL_MESSAGE);
                          }}
                        />
                        Default text
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 400, fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name="approvalTextMode"
                          checked={approvalTextMode === 'custom'}
                          onChange={() => {
                            setApprovalTextMode('custom');
                            setDraftComments('');
                          }}
                        />
                        Type text
                      </label>
                    </div>
                  )}
                  <textarea
                    id="decisionComments"
                    rows={draftStatus === ApplicationStatus.APPROVED && approvalTextMode === 'default' ? 10 : 3}
                    value={draftComments}
                    onChange={(e) => {
                      setDraftComments(e.target.value);
                      if (draftStatus === ApplicationStatus.APPROVED) setApprovalTextMode('custom');
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save Decision'}
                  </button>
                  <button className="btn btn-outline" onClick={() => setSelectedId(null)}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </Modal>
        )}

        {deletingApplication && (
          <Modal title="Delete Application" onClose={() => setDeletingApplication(null)}>
            <p className="alert-error" style={{ padding: '0.85rem' }}>
              Delete <strong>{deletingApplication.applicantName}</strong>'s application for{' '}
              <strong>{deletingApplication.scholarshipName}</strong>? This permanently removes the application, its
              uploaded documents, and its review history. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                className="btn btn-primary"
                type="button"
                style={{ background: 'var(--error-text)' }}
                disabled={deleting}
                onClick={handleConfirmDelete}
              >
                {deleting ? 'Deleting...' : 'Delete Application'}
              </button>
              <button className="btn btn-outline" type="button" onClick={() => setDeletingApplication(null)} disabled={deleting}>
                Cancel
              </button>
            </div>
          </Modal>
        )}

      </div>
    </div>
  );
};

export default ApplicationReviewPage;
